import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { ZONE_OFFSET_TO_TIMEZONE_ABBREVIATION } from '../constants/timezone';

const OFFSET_TO_TIMEZONE_ABBREVIATION = {
  '+08:00': 'AWST',
  '+08:45': 'ACWST',
  '+09:30': 'ACST',
  '+10:00': 'AEST',
  '+10:30': 'ACDT',
  '+11:00': 'AEDT',
  '+12:00': 'NFDT',
};

const APPOINTMENT_TYPE_LABEL = {
  atHome: 'Telehealth video consultation',
  atGpClinic: 'Telehealth video consultation',
  ageCare: 'Telehealth video consultation',
  f2f: 'In-person consultation',
};

const DATE_LABEL_FORMAT = 'dddd, D MMMM YYYY';
const TIME_LABEL_FORMAT = 'h:mm A';
const SHORT_DATE_TIME_FORMAT = 'ddd D MMM YYYY, h:mm A';
const PAID_AT_FORMAT = 'D MMM YYYY, h:mm A';

export const parseAppointmentMoment = (startDateTime) => {
  if (!startDateTime) return null;

  const normalised = String(startDateTime).replace(' +', '+');
  const parsed = moment.parseZone(normalised, moment.ISO_8601);

  return parsed.isValid() ? parsed : null;
};

export const getTimezoneAbbreviation = (appointmentMoment) => {
  if (!appointmentMoment) return '';

  const offset = appointmentMoment.format('Z');
  // The zone name, when the moment was put in one with moment-timezone's .tz(zone).
  const zone =
    typeof appointmentMoment.tz === 'function'
      ? appointmentMoment.tz()
      : undefined;

  return (
    ZONE_OFFSET_TO_TIMEZONE_ABBREVIATION[zone]?.[offset] ||
    OFFSET_TO_TIMEZONE_ABBREVIATION[offset] ||
    `UTC${offset}`
  );
};

export const formatAppointmentDate = (startDateTime) => {
  const appointmentMoment = parseAppointmentMoment(startDateTime);

  return appointmentMoment ? appointmentMoment.format(DATE_LABEL_FORMAT) : '';
};

export const formatAppointmentTime = (startDateTime) => {
  const appointmentMoment = parseAppointmentMoment(startDateTime);
  if (!appointmentMoment) return '';

  return `${appointmentMoment.format(
    TIME_LABEL_FORMAT
  )} (${getTimezoneAbbreviation(appointmentMoment)})`;
};

export const formatAppointmentMomentLabel = (appointmentMoment) => {
  if (!appointmentMoment) return '';

  return `${appointmentMoment.format(
    SHORT_DATE_TIME_FORMAT
  )} (${getTimezoneAbbreviation(appointmentMoment)})`;
};

export const formatAppointmentDateTime = (startDateTime) =>
  formatAppointmentMomentLabel(parseAppointmentMoment(startDateTime));

/**
 * The stored start shown in `timezone` (the zone the patient picked), with that zone's
 * label, so it matches the slots they chose from. Without a known zone, as stored.
 */
export const formatAppointmentDateTimeInZone = (startDateTime, timezone) => {
  const appointmentMoment = parseAppointmentMoment(startDateTime);
  if (!appointmentMoment) return '';

  return formatAppointmentMomentLabel(
    timezone && momentTimezone.tz.zone(timezone)
      ? momentTimezone(appointmentMoment).tz(timezone)
      : appointmentMoment
  );
};

export const formatAppointmentTypeLabel = (appointmentType) =>
  APPOINTMENT_TYPE_LABEL[appointmentType] || '';

export const formatPaymentTimestamp = (timestamp) => {
  if (!timestamp) return '';

  const parsed = moment(timestamp, moment.ISO_8601);

  return parsed.isValid() ? parsed.format(PAID_AT_FORMAT) : '';
};

export const formatAmountFromCents = (amountInCents) => {
  if (amountInCents === null || amountInCents === undefined) return '';

  const amount = Number(amountInCents) / 100;

  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '';
};
